import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { MessageCircle, Send, ChevronDown, ChevronUp } from "lucide-react";

interface Message {
  id: string;
  sender_user_id: string;
  message: string;
  is_read: boolean;
  created_at: string;
  sender_name?: string;
}

interface ItemWithMessages {
  id: string;
  name: string;
  images: string[] | null;
  messages: Message[];
  unread_count: number;
}

interface ShopMessagesTabProps {
  ptaId: string | null;
}

export function ShopMessagesTab({ ptaId }: ShopMessagesTabProps) {
  const { user } = useAuth();
  const [items, setItems] = useState<ItemWithMessages[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [sending, setSending] = useState<string | null>(null);

  useEffect(() => {
    if (ptaId) {
      fetchMessages();
      
      // Set up realtime subscription
      const channel = supabase
        .channel(`shop_messages_pta:${ptaId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'shop_messages'
          },
          () => {
            fetchMessages();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [ptaId]);

  const fetchMessages = async () => {
    if (!ptaId) return;
    setLoading(true);

    // Fetch PTA items with their messages
    const { data: ptaItems, error: itemsError } = await supabase
      .from('shop_items')
      .select('id, name, images')
      .eq('pta_id', ptaId)
      .eq('seller_type', 'pta')
      .eq('status', 'active');

    if (itemsError) {
      console.error('Error fetching items:', itemsError);
      setLoading(false);
      return;
    }

    // Fetch messages for these items
    const itemsWithMessages: ItemWithMessages[] = [];
    
    for (const item of ptaItems || []) {
      const { data: messages, error: msgError } = await supabase
        .from('shop_messages')
        .select('id, sender_user_id, message, is_read, created_at')
        .eq('shop_item_id', item.id)
        .order('created_at', { ascending: true });

      if (msgError) {
        console.error('Error fetching messages:', msgError);
        continue;
      }

      if (messages && messages.length > 0) {
        // Get sender names
        const messagesWithNames = await Promise.all(
          messages.map(async (msg) => {
            const { data: profile } = await supabase
              .from('profiles')
              .select('full_name, display_name')
              .eq('id', msg.sender_user_id)
              .single();

            return {
              ...msg,
              sender_name: profile?.display_name || profile?.full_name || 'Unknown'
            };
          })
        );

        const unreadCount = messagesWithNames.filter(
          m => !m.is_read && m.sender_user_id !== user?.id
        ).length;

        itemsWithMessages.push({
          ...item,
          messages: messagesWithNames,
          unread_count: unreadCount
        });
      }
    }

    // Sort by unread count (highest first), then by most recent message
    itemsWithMessages.sort((a, b) => {
      if (b.unread_count !== a.unread_count) {
        return b.unread_count - a.unread_count;
      }
      const aLatest = a.messages[a.messages.length - 1]?.created_at || '';
      const bLatest = b.messages[b.messages.length - 1]?.created_at || '';
      return bLatest.localeCompare(aLatest);
    });

    setItems(itemsWithMessages);
    setLoading(false);
  };

  const toggleExpand = async (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
      // Mark messages as read
      await supabase
        .from('shop_messages')
        .update({ is_read: true })
        .eq('shop_item_id', itemId)
        .neq('sender_user_id', user?.id || '');
      
      // Refresh to update unread counts
      fetchMessages();
    }
    setExpandedItems(newExpanded);
  };

  const handleSendReply = async (itemId: string) => {
    const message = replyText[itemId]?.trim();
    if (!message || !user) return;

    if (message.length > 500) {
      toast({
        title: "Message too long",
        description: "Maximum 500 characters allowed",
        variant: "destructive"
      });
      return;
    }

    setSending(itemId);

    const { error } = await supabase
      .from('shop_messages')
      .insert({
        shop_item_id: itemId,
        sender_user_id: user.id,
        message
      });

    if (error) {
      toast({
        title: "Failed to send message",
        description: error.message,
        variant: "destructive"
      });
    } else {
      setReplyText(prev => ({ ...prev, [itemId]: '' }));
      fetchMessages();
    }

    setSending(null);
  };

  const totalUnread = items.reduce((sum, item) => sum + item.unread_count, 0);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Messages
              {totalUnread > 0 && (
                <Badge variant="destructive" className="rounded-full">
                  {totalUnread} new
                </Badge>
              )}
            </CardTitle>
            <CardDescription>Messages from parents about your PTA items</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No messages yet</p>
            <p className="text-sm">When parents message about your items, they'll appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map(item => (
              <div key={item.id} className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleExpand(item.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {item.images?.[0] && (
                      <img 
                        src={item.images[0]} 
                        alt={item.name} 
                        className="w-12 h-12 object-cover rounded" 
                      />
                    )}
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{item.name}</h4>
                        {item.unread_count > 0 && (
                          <Badge variant="destructive" className="rounded-full text-xs">
                            {item.unread_count}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {item.messages.length} message{item.messages.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  {expandedItems.has(item.id) ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </button>

                {expandedItems.has(item.id) && (
                  <div className="border-t p-4 bg-muted/20">
                    <ScrollArea className="h-64 pr-4 mb-4">
                      <div className="space-y-3">
                        {item.messages.map(msg => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.sender_user_id === user?.id ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[80%] rounded-lg p-3 ${
                              msg.sender_user_id === user?.id
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-background border'
                            }`}>
                              <div className="text-xs font-semibold mb-1 opacity-80">
                                {msg.sender_user_id === user?.id ? 'You' : msg.sender_name}
                              </div>
                              <div className="text-sm">{msg.message}</div>
                              <div className="text-xs opacity-60 mt-1">
                                {new Date(msg.created_at).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    
                    <div className="flex gap-2">
                      <Textarea
                        value={replyText[item.id] || ''}
                        onChange={(e) => setReplyText(prev => ({ ...prev, [item.id]: e.target.value }))}
                        placeholder="Type your reply... (max 500 characters)"
                        className="min-h-[60px]"
                        maxLength={500}
                      />
                      <Button
                        onClick={() => handleSendReply(item.id)}
                        disabled={!replyText[item.id]?.trim() || sending === item.id}
                        size="icon"
                        className="h-[60px] w-[60px]"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="text-xs text-muted-foreground text-right mt-1">
                      {(replyText[item.id] || '').length}/500
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
